<template>
  <b-card :header="$t('project.manageProjects')">
    <!-- User Interface controls -->
    <b-row>
      <b-col md="6" class="my-1">
        <b-form-group label-cols-sm="3" :label="$t('other.filter')" class="mb-0">
          <b-input-group>
            <b-form-input v-model="filter" :placeholder="$t('other.searchHolder')"></b-form-input>
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''">{{ $t('other.clear') }}</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>

      <b-col md="6" class="my-1">
        <b-form-group label-cols-sm="3" :label="$t('other.perPage')" class="mb-0">
          <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <section v-if="errored !== null">
      <p>{{ $t('other.retrDataError') }}</p>
      <b-alert variant="danger" show>{{errored}}</b-alert>
    </section>

    <section v-else>
      <div v-if="loading">{{ $t('other.loading') }}</div>
      <div v-else>
        <!-- Main table element -->
        <b-table
          show-empty
          stacked="md"
          :items="projects"
          :fields="fields"
          :current-page="currentPage"
          :per-page="perPage"
          :filter="filter"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          @filtered="onFiltered"
        >
          <template #cell(prjstatus)="row">
            <b-badge :variant="getBadgeButton(row.item.item1.status)">{{getBadge(row.item.item1.status)}}</b-badge>
          </template>

          <template #cell(actions)="row">
            <router-link class="mr-1" v-if="row.item.item1.status == 1" :to="openProjectLink(row.item.item1.id)">
              <b-button
                size="sm"
                v-b-tooltip.hover
                :title="$t('project.openProject')"
                variant="warning"
              >
                <i class="icon-eyeglass" />
              </b-button>
            </router-link>
            <router-link class="mr-1" v-if="row.item.item1.status == 1 && decideAetAdminRights(row)" :to="exportCurrentAlarmsLink(row.item.item1.id)">
              <b-button
                size="sm"
                v-b-tooltip.hover
                :title="$t('project.alarmsExport.currentAlarms')"
                variant="danger"
              >
                <i class="icon-bell" />
              </b-button>
            </router-link>
            <router-link class="mr-1" v-if="row.item.item1.status == 1 && decideAetAdminRights(row)" :to="exportEventsLink(row.item.item1.id)">
              <b-button
                size="sm"
                v-b-tooltip.hover
                :title="$t('project.eventsExport.eventsProject')"
                variant="outline-info"
              >
                <i class="icon-note" />
              </b-button>
            </router-link>
            <router-link class="mr-1" v-if="row.item.item1.status == 1 && decideAetAdminRights(row)" :to="exportTrendsLink(row.item.item1.id)">
              <b-button
                size="sm"
                v-b-tooltip.hover
                :title="$t('project.trendsExport.trendsProject')"
                variant="outline-primary"
              >
                <i class="icon-notebook" />
              </b-button>
            </router-link>
            <router-link class="mr-1" v-if="row.item.item1.status == 1 && decideAetAdminRights(row)" :to="exportAlarmsLink(row.item.item1.id)">
              <b-button
                size="sm"
                v-b-tooltip.hover
                :title="$t('project.alarmsExport.alarmsProject')"
                variant="outline-danger"
              >
                <i class="icon-bell" />
              </b-button>
            </router-link>
            <router-link class="mr-1" v-if="row.item.item1.status == 1 && decideTimeScheduleAdminRights(row)" :to="exportSchedulesLink(row.item.item1.id)">
              <b-button
                size="sm"
                v-b-tooltip.hover
                :title="$t('project.schedulesExport.schedulesProject')"
                variant="outline-dark"
              >
                <i class="icon-clock" />
              </b-button>
            </router-link>
          </template>
        </b-table>
      </div>
    </section>

    <div v-if="informed">
      <b-alert show>{{informed}}</b-alert>
    </div>

    <b-row>
      <b-col md="6" class="my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          class="my-0"
        ></b-pagination>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { projectService } from "@/_services";
import { errorHandler } from "@/_helpers";

export default {
  name: "MyProjects",
  data() {
    return {
      user: {},
      projects: [],
      loading: true,
      errored: null,
      informed: "",
      fields: [
        {
          key: "item1.projectname",
          label: this.$i18n.t("project.projListName"),
          sortable: true,
          sortDirection: "desc"
        },
        { key: "item1.location", label: this.$i18n.t("project.projLocation") },
        { key: "prjstatus", label: "Status" },
        { key: "actions", label: this.$i18n.t("project.projListAct") }
      ],
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 15, 20],
      sortBy: null,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      totalRows: null
    };
  },
  created() {
    this.user = JSON.parse(localStorage.getItem("viewer-user"));

    projectService
      .getUserProjects(this.user.id)
      .then(response => {
        this.projects = response.data;
      })
      .catch(error => {
        this.errored = errorHandler(error);
      })
      .finally(() => {
        this.loading = false;
        this.totalRows = this.projects.length;
      });
  },
  methods: {
    getBadgeButton(status) {
      switch (status) {
        case 0:
          return "info";
        case 1:
          return "success";
        case 2:
          return "warning";
        default:
          return "danger";
      }
    },
    getBadge(status) {
      switch (status) {
        case 0:
          return this.$i18n.t("project.projStatusNew");
        case 1:
          return this.$i18n.t("project.projStatusActive");
        case 2:
          return this.$i18n.t("project.projStatusEdit");
        default:
          return this.$i18n.t("project.projStatusSuspend");
      }
    },
    decideAetAdminRights(row) {
      return row.item.item2 > 1;
    },
    decideTimeScheduleAdminRights(row) {
      return row.item.item2 == 1 || row.item.item2 == 2;
    },
    openProjectLink(projectId) {
      return `/project/view/${projectId}`;
    },
    exportCurrentAlarmsLink(projectId) {
      return `/project/alarms/current/${projectId}`;
    },
    exportAlarmsLink(projectId) {
      return `/project/alarms/${projectId}`;
    },
    exportEventsLink(projectId) {
      return `/project/events/${projectId}`;
    },
    exportTrendsLink(projectId) {
      return `/project/trends/${projectId}`;
    },
    exportSchedulesLink(projectId) {
      return `/project/schedules/${projectId}`;
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  }
};
</script>
